import { useEffect, useMemo, useState } from "react";
import { usePermissions } from "../context/PermissionContext";
import { toast } from "react-toastify";
import api from "../api";

// Shared BUSINESS/DATA logic for the "Assign Form" feature -- API calls,
// permission flags, filters, pagination, summary/table state, and the
// Assign Form / manage-users modal state machines. Extracted so
// AdminFormAssignmentsPage.jsx and RoleFormAssignmentsPage.jsx can each own
// their OWN page JSX/layout while calling this ONE hook for everything
// data-related -- neither page duplicates a fetch, a save handler, or a
// validation rule; they only differ in how they PRESENT this same state.
export const STATUS_VARIANTS = { DRAFT: "secondary", ACTIVE: "success", CLOSED: "dark", CANCELLED: "danger", ARCHIVED: "secondary" };

const initialForm = {
  project_id: "",
  project_module_id: "",
  form_type_id: "",
  form_id: "",
  form_version_id: "",
  user_ids: [],
  due_at: "",
  assignment_title: "",
  instructions: "",
  is_mandatory: false,
  allow_multiple_submissions: false,
  maximum_submissions: "",
  allow_save_draft: true,
  allow_late_submission: false,
  department_id: "",
  related_task_id: "",
  reviewer_id: "",
  approver_id: "",
  approval_required: false,
  // Knowledge Sharing subject-selection hardening (requirement 4): lets an
  // assigner who already knows who a survey/session is ABOUT lock it in at
  // creation time (form_assignments.locked_subject_user_id) -- "" (never
  // selected) means unlocked, matching every other optional-id field above.
  locked_subject_user_id: "",
};

export default function useFormAssignments() {
  const { can, canAny, loading: permissionsLoading } = usePermissions();

  const hasAnyAssignmentAccess = canAny([
    { moduleKey: "form_assignments", actionKey: "create" },
    { moduleKey: "form_assignments", actionKey: "view" },
    { moduleKey: "form_assignments", actionKey: "view_all" },
    { moduleKey: "form_assignments", actionKey: "manage_all" },
  ]);

  const [assignments, setAssignments] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const [projectFilterOptions, setProjectFilterOptions] = useState([]);
  const [users, setUsers] = useState([]); // for the "manage users" modal only (already-assigned lookups)

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [projectFilter, setProjectFilter] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 20;

  const [form, setForm] = useState(initialForm);
  const [selectedFormRow, setSelectedFormRow] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const [usersModalTarget, setUsersModalTarget] = useState(null);
  const [removeUserTarget, setRemoveUserTarget] = useState(null);
  const [removingUser, setRemovingUser] = useState(false);
  const [assignedUsers, setAssignedUsers] = useState([]);
  const [addUserIds, setAddUserIds] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);

  // ---------------------------------------------------------------------
  // Phase 16 (Public Form Links), §28/§29/§30. Per-assignee public-link
  // management (Generate/Regenerate/Revoke) inside the SAME "Assigned
  // Employees" modal, plus the one-time "here are the links" surface
  // right after creating an assignment -- the backend only ever returns a
  // raw URL once (generate/regenerate/create/add-users response), so
  // there is nothing to "recover" later; the modal always re-fetches
  // link STATUS (has_public_link/expires_at/revoked_at), never a raw URL.
  // ---------------------------------------------------------------------
  const [linkBusyId, setLinkBusyId] = useState(null);
  const [justAssignedLinks, setJustAssignedLinks] = useState(null); // [{ assignment_user_id, user_id, user_name, url }] | null

  // The full absolute URL a Generate/Regenerate call just returned, keyed by
  // assignmentUserId -- deliberately in-memory/per-session ONLY (never
  // fetched back from the server -- only public_token_hash is persisted, see
  // services/publicFormTokenService.js's own header comment), so
  // PublicLinkSummaryCell/ManageAssignedUsersModal can show "here's the link
  // you just generated" right after the action, and correctly show nothing
  // for it again after a page refresh.
  const [generatedLinks, setGeneratedLinks] = useState({});

  const copyToClipboard = async (text) => {
    if (!text) return false;
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
        return true;
      }
    } catch (err) {
      // fall through to the manual-copy fallback below
    }
    try {
      const el = document.createElement("textarea");
      el.value = text;
      el.style.position = "fixed";
      el.style.opacity = "0";
      document.body.appendChild(el);
      el.focus();
      el.select();
      document.execCommand("copy");
      document.body.removeChild(el);
      return true;
    } catch (err) {
      return false;
    }
  };

  // `assignmentId` is now an explicit argument (was implicitly
  // usersModalTarget.id) so these same three functions work both from
  // inside the "Assigned Employees" modal AND directly from a table row's
  // own "Public Link" cell, where no modal is open at all -- e.g. the
  // single-recipient inline actions on RoleFormAssignmentsPage.jsx /
  // AdminFormAssignmentsPage.jsx. Always refreshes the assignment LIST
  // (so a row's badge/summary updates immediately) and additionally
  // refreshes the modal's own assignee list when it happens to be open
  // for this same assignment, so both surfaces stay in sync regardless of
  // which one triggered the change.
  const refreshAfterLinkChange = (assignmentId) => {
    if (usersModalTarget && Number(usersModalTarget.id) === Number(assignmentId)) {
      openUsersModal(usersModalTarget);
    }
    fetchAssignments();
    fetchAssignedByMe();
  };

  const generatePublicLink = async (assignmentId, assignmentUserId) => {
    setLinkBusyId(assignmentUserId);
    try {
      const res = await api.post(`/form-assignments/${assignmentId}/users/${assignmentUserId}/public-link`);
      const url = res.data?.data?.url;
      const copied = await copyToClipboard(url);
      toast.success(copied ? "Public link generated and copied to clipboard" : "Public link generated");
      if (url) setGeneratedLinks((prev) => ({ ...prev, [assignmentUserId]: url }));
      refreshAfterLinkChange(assignmentId);
    } catch (err) {
      const data = err.response?.data;
      if (data?.code === "PUBLIC_LINK_ALREADY_ACTIVE") {
        toast.error("An active link already exists — use Regenerate instead.");
      } else {
        toast.error(data?.message || "Failed to generate public link");
      }
    } finally {
      setLinkBusyId(null);
    }
  };

  // `confirmMessage` is supplied by the caller (ManageAssignedUsersModal /
  // PublicLinkSummaryCell) because the right copy — and whether a
  // confirmation is even needed — depends on the CURRENT link state the
  // caller already knows (§12): regenerating an ACTIVE link warns that the
  // old one dies immediately; issuing a fresh one for an already-expired/
  // revoked link has nothing live to lose, so callers pass no message for
  // that case.
  const regeneratePublicLink = async (assignmentId, assignmentUserId, { confirmMessage } = {}) => {
    if (confirmMessage && !window.confirm(confirmMessage)) return;
    setLinkBusyId(assignmentUserId);
    try {
      const res = await api.post(`/form-assignments/${assignmentId}/users/${assignmentUserId}/public-link/regenerate`);
      const url = res.data?.data?.url;
      const copied = await copyToClipboard(url);
      toast.success(copied ? "Public link regenerated and copied to clipboard" : "Public link regenerated");
      if (url) setGeneratedLinks((prev) => ({ ...prev, [assignmentUserId]: url }));
      refreshAfterLinkChange(assignmentId);
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to regenerate public link");
    } finally {
      setLinkBusyId(null);
    }
  };

  const revokePublicLink = async (assignmentId, assignmentUserId, { confirmMessage } = {}) => {
    if (confirmMessage && !window.confirm(confirmMessage)) return;
    setLinkBusyId(assignmentUserId);
    try {
      await api.delete(`/form-assignments/${assignmentId}/users/${assignmentUserId}/public-link`);
      toast.success("Public link revoked");
      setGeneratedLinks((prev) => {
        if (!(assignmentUserId in prev)) return prev;
        const next = { ...prev };
        delete next[assignmentUserId];
        return next;
      });
      refreshAfterLinkChange(assignmentId);
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to revoke public link");
    } finally {
      setLinkBusyId(null);
    }
  };

  const closeJustAssignedLinks = () => setJustAssignedLinks(null);

  // ---------------------------------------------------------------------
  // Admin redesign additions below. All additive -- nothing above this
  // point changed shape, so RoleFormAssignmentsPage.jsx (which destructures
  // a subset of this same hook) is completely unaffected. Every new piece
  // reuses the EXACT same endpoints/validators the original create/status/
  // recipient flows already use -- no new backend surface.
  // ---------------------------------------------------------------------

  // View mode ("table" vs "dragdrop") + the lightweight client-side tabs
  // that narrow the already-loaded `assignments` page. "Assigned By Me"
  // swaps the data source to the real, separate GET /form-assignments/
  // assigned-by-me endpoint (it is NOT a query param on the main list) --
  // this is the ONLY creator-scoped (form_assignments.created_by = actor)
  // dataset in this hook; `assignments`/`fetchAssignments` below stays
  // permission/data-scope visibility (may include assignments created by
  // someone else) and must never be used to answer "did I create this."
  // "Submitted"/"Overdue" filter the CURRENT page's rows using the
  // recipient-progress counts the list endpoint already returns per row
  // (submitted_count/assigned_count/overdue_count) -- no new aggregation.
  const [viewMode, setViewMode] = useState("table");
  const [activeTab, setActiveTab] = useState("ALL");

  const [assignedByMeAssignments, setAssignedByMeAssignments] = useState([]);
  const [assignedByMeTotal, setAssignedByMeTotal] = useState(0);
  const [assignedByMeLoading, setAssignedByMeLoading] = useState(false);

  // Shares the SAME search/status/project/page/pageSize state as
  // fetchAssignments below, so a caller (e.g. RoleFormAssignmentsPage's
  // "Assigned By Me" table) gets server-side filtering/pagination WITHIN
  // the creator-scoped dataset, never a client-side filter over a
  // differently-scoped list (§10 of the Assigned-By-Me scope fix).
  const fetchAssignedByMe = async () => {
    setAssignedByMeLoading(true);
    try {
      const res = await api.get("/form-assignments/assigned-by-me", {
        params: {
          search: search || undefined,
          status: statusFilter || undefined,
          project_id: projectFilter || undefined,
          page: currentPage,
          pageSize,
        },
      });
      setAssignedByMeAssignments(Array.isArray(res.data?.data) ? res.data.data : []);
      setAssignedByMeTotal(res.data?.meta?.total || 0);
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to fetch your assignments");
    } finally {
      setAssignedByMeLoading(false);
    }
  };

  // Fetched on the SAME triggers as fetchAssignments (mount + whenever a
  // filter/page changes) -- not gated on activeTab -- so the "Assigned By
  // Me" KPI (services/formAssignmentDashboardService.js's getAssignerSummary,
  // also a bare `created_by = actor` count) and this table can never drift
  // out of sync the way the generic `assignments` list previously did.
  useEffect(() => {
    fetchAssignedByMe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, statusFilter, projectFilter, currentPage, pageSize]);

  const assignedByMeTotalPages = Math.max(Math.ceil(assignedByMeTotal / pageSize), 1);

  // The rows the table actually renders for the current tab. "ALL" uses the
  // server-paginated `assignments` as-is (unchanged behavior). The other
  // tabs work off whichever source is relevant and narrow it client-side --
  // "Assigned By Me" has its own full (non-paginated, capped at 200) source
  // so that narrowing never hides rows sitting on a different server page.
  const visibleAssignments = useMemo(() => {
    if (activeTab === "ASSIGNED_BY_ME") return assignedByMeAssignments;
    if (activeTab === "SUBMITTED") return assignments.filter((a) => Number(a.assigned_count) > 0 && Number(a.submitted_count) >= Number(a.assigned_count));
    if (activeTab === "OVERDUE") return assignments.filter((a) => Number(a.overdue_count) > 0);
    return assignments;
  }, [activeTab, assignments, assignedByMeAssignments]);

  const visibleLoading = activeTab === "ASSIGNED_BY_ME" ? assignedByMeLoading : loading;

  // ---------------------------------------------------------------------
  // Row expansion (replaces stacking every action button in the row) --
  // reuses the existing assignment-detail endpoint, the same one the old
  // "Recipients" button's data would need anyway.
  // ---------------------------------------------------------------------
  const [expandedRowId, setExpandedRowId] = useState(null);
  const [expandedDetail, setExpandedDetail] = useState(null);
  const [expandedLoading, setExpandedLoading] = useState(false);

  const toggleRowExpand = async (row) => {
    if (expandedRowId === row.id) {
      setExpandedRowId(null);
      setExpandedDetail(null);
      return;
    }
    setExpandedRowId(row.id);
    setExpandedDetail(null);
    setExpandedLoading(true);
    try {
      const res = await api.get(`/form-assignments/${row.id}`);
      setExpandedDetail(res.data?.data || null);
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to load assignment details");
    } finally {
      setExpandedLoading(false);
    }
  };

  // ---------------------------------------------------------------------
  // Edit Assignment -- a real, existing PUT /form-assignments/:id endpoint
  // that had no frontend UI calling it at all before this redesign. Fields
  // mirror exactly what the backend documents as editable (everything the
  // create payload has, minus form_version_id/user_ids, which the backend
  // does not allow changing after creation). "Extend Due Date" in the
  // actions menu just opens this same modal -- there is no separate
  // lightweight endpoint for a due-date-only patch, and sending a partial
  // payload here risks tripping the same required-field validation the
  // create endpoint has, so it reuses this one known-working path instead.
  // ---------------------------------------------------------------------
  const [editTarget, setEditTarget] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const [editSaving, setEditSaving] = useState(false);

  const openEdit = (row) => {
    setEditTarget(row);
    setEditForm({
      assignment_title: row.assignment_title || "",
      instructions: row.instructions || "",
      due_at: row.due_at ? String(row.due_at).slice(0, 16) : "",
      is_mandatory: !!row.is_mandatory,
      allow_multiple_submissions: !!row.allow_multiple_submissions,
      maximum_submissions: row.maximum_submissions ?? "",
      allow_save_draft: !!row.allow_save_draft,
      allow_late_submission: !!row.allow_late_submission,
      department_id: row.department_id || "",
      related_task_id: row.related_task_id || "",
      reviewer_id: row.reviewer_id || "",
      approver_id: row.approver_id || "",
      approval_required: !!row.approval_required,
      locked_subject_user_id: row.locked_subject_user_id || "",
    });
  };

  const closeEdit = () => {
    setEditTarget(null);
    setEditForm(null);
  };

  const handleEditFormChange = (field, value) => setEditForm((prev) => ({ ...prev, [field]: value }));

  const handleUpdateAssignment = async () => {
    if (!editTarget || !editForm) return;
    if (!editForm.assignment_title.trim()) {
      toast.warning("Assignment title is required");
      return;
    }
    setEditSaving(true);
    try {
      const payload = {
        assignment_title: editForm.assignment_title.trim(),
        instructions: editForm.instructions.trim() || null,
        due_at: editForm.due_at || null,
        is_mandatory: editForm.is_mandatory,
        allow_multiple_submissions: editForm.allow_multiple_submissions,
        maximum_submissions: editForm.maximum_submissions === "" ? null : Number(editForm.maximum_submissions),
        allow_save_draft: editForm.allow_save_draft,
        allow_late_submission: editForm.allow_late_submission,
        department_id: editForm.department_id || null,
        related_task_id: editForm.related_task_id || null,
        reviewer_id: editForm.reviewer_id || null,
        approver_id: editForm.approver_id || null,
        approval_required: editForm.approval_required,
        locked_subject_user_id: editForm.locked_subject_user_id || null,
      };
      await api.put(`/form-assignments/${editTarget.id}`, payload);
      toast.success("Assignment updated");
      closeEdit();
      fetchAssignments();
      fetchAssignedByMe();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to update assignment");
    } finally {
      setEditSaving(false);
    }
  };

  // ---------------------------------------------------------------------
  // Drag & Drop mode's own data. Deliberately fetched ONLY when viewMode
  // becomes "dragdrop" (never on initial mount) -- AdminFormAssignmentsPage
  // and RoleFormAssignmentsPage are asserted elsewhere to call the exact
  // same set of endpoints on mount, and this redesign must not add a
  // fourth/fifth unconditional call that only Admin's page makes.
  // Recipient targets need NO new fetch at all: `users` (already loaded
  // unconditionally for the existing "manage users" modal) already carries
  // department_name/designation_name per user, so department/designation/
  // "All Staff" groupings are computed client-side from data already in
  // memory -- never a fabricated bulk-assign target type the backend
  // doesn't support (assignment is always, ultimately, a user_ids[] array).
  // ---------------------------------------------------------------------
  const [dragForms, setDragForms] = useState([]);
  const [dragFormsLoading, setDragFormsLoading] = useState(false);
  const [dragFormTypeOptions, setDragFormTypeOptions] = useState([]);
  const [dragFormSearch, setDragFormSearch] = useState("");
  const [dragFormTypeFilter, setDragFormTypeFilter] = useState("");
  const [dragProjectFilter, setDragProjectFilter] = useState("");
  const [dragModuleFilter, setDragModuleFilter] = useState("");

  const fetchDragForms = async () => {
    setDragFormsLoading(true);
    try {
      const res = await api.get("/form-assignment-options/forms", {
        params: {
          project_id: dragProjectFilter || undefined,
          project_module_id: dragModuleFilter || undefined,
          form_type_id: dragFormTypeFilter || undefined,
          pageSize: 200,
        },
      });
      setDragForms(Array.isArray(res.data?.data) ? res.data.data : []);
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to load published forms");
    } finally {
      setDragFormsLoading(false);
    }
  };

  useEffect(() => {
    if (viewMode !== "dragdrop") return;
    fetchDragForms();
    if (dragFormTypeOptions.length === 0) {
      api.get("/form-assignment-options/form-types", {
        params: { project_id: dragProjectFilter || undefined, project_module_id: dragModuleFilter || undefined },
      })
        .then((res) => setDragFormTypeOptions(Array.isArray(res.data?.data) ? res.data.data : []))
        .catch((err) => console.error("Fetch assignable form types error:", err));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMode, dragProjectFilter, dragModuleFilter, dragFormTypeFilter]);

  const visibleDragForms = useMemo(() => {
    const q = dragFormSearch.trim().toLowerCase();
    if (!q) return dragForms;
    return dragForms.filter((f) => `${f.name} ${f.form_type_name || ""} ${f.project_name || ""}`.toLowerCase().includes(q));
  }, [dragForms, dragFormSearch]);

  // Department groups for the right-hand recipient panel, derived purely
  // from the already-loaded eligible-users list -- see comment above.
  const departmentGroups = useMemo(() => {
    const byDept = new Map();
    users.forEach((u) => {
      const key = u.department_name || "No Department";
      if (!byDept.has(key)) byDept.set(key, []);
      byDept.get(key).push(u);
    });
    return [...byDept.entries()]
      .map(([name, list]) => ({ key: `dept:${name}`, name, users: list }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [users]);

  // Staged drop: the form + recipients a drag-and-drop (or its keyboard/
  // click "Assign" fallback) prepared, BEFORE anything is confirmed/saved --
  // opening the existing AssignFormModal pre-filled, never auto-saving on
  // drop. Reuses the exact same `form`/`showModal`/`handleSave` state the
  // normal "+ Assign Form" button already drives -- one save path, one
  // validation path, for both entry points.
  const stageAssignmentFromDrop = (formRow, userIds) => {
    setForm({
      ...initialForm,
      project_id: formRow?.project_id ? String(formRow.project_id) : "",
      project_module_id: formRow?.project_module_id ? String(formRow.project_module_id) : "",
      form_type_id: formRow?.form_type_id ? String(formRow.form_type_id) : "",
      form_id: String(formRow.id),
      form_version_id: formRow.published_version_id ? String(formRow.published_version_id) : "",
      user_ids: [...new Set(userIds.map(Number))],
      assignment_title: `${formRow.name} — assignment`,
    });
    setSelectedFormRow(formRow);
    setShowModal(true);
  };

  const fetchAssignments = async () => {
    try {
      setLoading(true);
      const res = await api.get("/form-assignments", {
        params: {
          search: search || undefined,
          status: statusFilter || undefined,
          project_id: projectFilter || undefined,
          page: currentPage,
          pageSize,
        },
      });
      setAssignments(Array.isArray(res.data?.data) ? res.data.data : []);
      setTotal(res.data?.meta?.total || 0);
    } catch (err) {
      console.error("Fetch form assignments error:", err);
      toast.error(err.response?.data?.message || "Failed to fetch form assignments");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAssignments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, statusFilter, projectFilter, currentPage, pageSize]);

  useEffect(() => {
    api.get("/form-assignment-options/projects", { params: { pageSize: 200 } })
      .then((res) => setProjectFilterOptions(Array.isArray(res.data?.data) ? res.data.data : []))
      .catch((err) => console.error("Fetch assignable projects error:", err));
    api.get("/form-assignment-options/users", { params: { pageSize: 500 } })
      .then((res) => setUsers(Array.isArray(res.data?.data) ? res.data.data : []))
      .catch((err) => console.error("Fetch assignable users error:", err));
  }, []);

  const totalPages = Math.max(Math.ceil(total / pageSize), 1);

  const handleChange = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  const openAdd = () => {
    setForm(initialForm);
    setSelectedFormRow(null);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setForm(initialForm);
    setSelectedFormRow(null);
  };

  const handleFormChange = (formId, formRow) => {
    handleChange("form_id", formId);
    handleChange("form_version_id", formRow?.published_version_id || "");
    setSelectedFormRow(formRow || null);
  };

  const handleSave = async () => {
    if (!form.project_id) {
      toast.warning("Please select a project");
      return;
    }
    if (!form.form_version_id) {
      toast.warning("Please select a published form");
      return;
    }
    if (form.user_ids.length === 0) {
      toast.warning("Select at least one employee to assign this form to");
      return;
    }
    if (!form.assignment_title.trim()) {
      toast.warning("Assignment title is required");
      return;
    }

    const payload = {
      assignment_title: form.assignment_title.trim(),
      instructions: form.instructions.trim() || null,
      form_version_id: Number(form.form_version_id),
      department_id: form.department_id || null,
      project_id: form.project_id || null,
      project_module_id: form.project_module_id || null,
      related_task_id: form.related_task_id || null,
      due_at: form.due_at || null,
      allow_multiple_submissions: form.allow_multiple_submissions,
      maximum_submissions: form.maximum_submissions === "" ? null : Number(form.maximum_submissions),
      allow_save_draft: form.allow_save_draft,
      allow_late_submission: form.allow_late_submission,
      is_mandatory: form.is_mandatory,
      reviewer_id: form.reviewer_id || null,
      approver_id: form.approver_id || null,
      approval_required: form.approval_required,
      locked_subject_user_id: form.locked_subject_user_id || null,
      user_ids: form.user_ids,
    };

    try {
      setSaving(true);
      const res = await api.post("/form-assignments", payload);
      toast.success(res.data?.message || `Form assigned successfully to ${form.user_ids.length} user(s).`);
      // Phase 16, §30: the raw one-time public-link URLs ride along on this
      // SAME response (assignment.public_links) — the backend can never
      // reconstruct them again later, so this is the only chance to show
      // "Copy Link" per assignee. assigned_users carries the display name
      // to zip each link back to a person.
      const assignedUsersList = res.data?.data?.assigned_users || [];
      const publicLinks = res.data?.data?.public_links || [];
      if (publicLinks.length) {
        const nameByAssignmentUserId = new Map(assignedUsersList.map((u) => [u.id, u.user_name]));
        setJustAssignedLinks(
          publicLinks.map((pl) => ({ ...pl, user_name: nameByAssignmentUserId.get(pl.assignment_user_id) || null }))
        );
      }
      closeModal();
      fetchAssignments();
      fetchAssignedByMe();
    } catch (err) {
      console.error("Create form assignment error:", err);
      const data = err.response?.data;
      if (data?.code === "FORM_ASSIGNMENT_DUPLICATE_USER") {
        toast.error("The same user was selected more than once.");
      } else if (data?.code === "FORM_ASSIGNMENT_PROJECT_ACCESS_DENIED") {
        toast.error("You do not have access to the selected project.");
      } else if (data?.code === "FORM_ASSIGNMENT_FORM_CONTEXT_MISMATCH") {
        toast.error("The selected form does not match the selected project/module.");
      } else if (data?.code === "FORM_VERSION_NOT_PUBLISHED") {
        toast.error("The selected form is not published.");
      } else {
        toast.error(data?.message || "Failed to assign form");
      }
    } finally {
      setSaving(false);
    }
  };

  const setStatus = async (row, status) => {
    setBusyId(row.id);
    try {
      await api.patch(`/form-assignments/${row.id}/status`, { status });
      toast.success(`Assignment ${status.toLowerCase()}`);
      fetchAssignments();
      fetchAssignedByMe();
    } catch (err) {
      toast.error(err.response?.data?.message || `Failed to set status to ${status}`);
    } finally {
      setBusyId(null);
    }
  };

  // Manage Assigned Employees modal's "Add Employees" directory --
  // deliberately a SEPARATE data source from `users`/`availableUsersToAdd`
  // below (the Assign Form wizard's own self-service "Assign To" picker,
  // unchanged). Backend-searched (never a client-side filter over a fixed
  // page — see GET /form-assignments/:assignmentId/eligible-users) so a
  // real active user outside the first loaded page is still found by
  // search, and already-active recipients of THIS assignment are excluded
  // server-side.
  const [eligibleUsers, setEligibleUsers] = useState([]);
  const [eligibleUsersTotal, setEligibleUsersTotal] = useState(0);
  const [eligibleUsersLoading, setEligibleUsersLoading] = useState(false);
  const [eligibleUsersPage, setEligibleUsersPage] = useState(1);
  const [eligibleUsersSearch, setEligibleUsersSearch] = useState("");
  const ELIGIBLE_USERS_PAGE_SIZE = 50;

  const fetchEligibleUsers = async (assignmentId, { search = "", page = 1, append = false } = {}) => {
    if (!assignmentId) return;
    setEligibleUsersLoading(true);
    try {
      const res = await api.get(`/form-assignments/${assignmentId}/eligible-users`, {
        params: { search: search || undefined, page, pageSize: ELIGIBLE_USERS_PAGE_SIZE },
      });
      const rows = Array.isArray(res.data?.data) ? res.data.data : [];
      setEligibleUsers((prev) => (append ? [...prev, ...rows] : rows));
      setEligibleUsersTotal(res.data?.meta?.total || 0);
      setEligibleUsersPage(page);
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to load the employee directory");
      if (!append) setEligibleUsers([]);
    } finally {
      setEligibleUsersLoading(false);
    }
  };

  // Called by the modal on every search-box keystroke (after its own
  // debounce) -- stores the query so a later refresh (add/remove) can
  // re-search with the same term instead of silently resetting it.
  const searchEligibleUsers = (query) => {
    setEligibleUsersSearch(query);
    if (!usersModalTarget) return;
    fetchEligibleUsers(usersModalTarget.id, { search: query, page: 1 });
  };

  const loadMoreEligibleUsers = () => {
    if (!usersModalTarget) return;
    fetchEligibleUsers(usersModalTarget.id, { search: eligibleUsersSearch, page: eligibleUsersPage + 1, append: true });
  };

  const openUsersModal = async (row) => {
    setUsersModalTarget(row);
    setAddUserIds([]);
    setEligibleUsersSearch("");
    setUsersLoading(true);
    fetchEligibleUsers(row.id, { search: "", page: 1 });
    try {
      const res = await api.get(`/form-assignments/${row.id}/users`);
      setAssignedUsers(Array.isArray(res.data?.data) ? res.data.data : []);
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to load assigned users");
    } finally {
      setUsersLoading(false);
    }
  };

  const closeUsersModal = () => {
    setUsersModalTarget(null);
    setAssignedUsers([]);
    setAddUserIds([]);
    setEligibleUsers([]);
    setEligibleUsersTotal(0);
    setEligibleUsersPage(1);
    setEligibleUsersSearch("");
  };

  const addUsersToAssignment = async () => {
    if (!usersModalTarget || addUserIds.length === 0) return;
    try {
      await api.post(`/form-assignments/${usersModalTarget.id}/users`, { user_ids: addUserIds });
      toast.success("Users assigned");
      setAddUserIds([]);
      openUsersModal(usersModalTarget);
      fetchAssignments();
      fetchAssignedByMe();
    } catch (err) {
      const data = err.response?.data;
      if (data?.code === "FORM_ASSIGNMENT_USER_EXISTS") {
        toast.error("One or more selected users are already assigned.");
      } else {
        toast.error(data?.message || "Failed to assign users");
      }
    }
  };

  const confirmRemoveUserFromAssignment = async () => {
    if (!usersModalTarget || !removeUserTarget) return;
    setRemovingUser(true);
    try {
      await api.delete(`/form-assignments/${usersModalTarget.id}/users/${removeUserTarget}`);
      toast.success("User removed");
      setRemoveUserTarget(null);
      openUsersModal(usersModalTarget);
      fetchAssignments();
      fetchAssignedByMe();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to remove user");
    } finally {
      setRemovingUser(false);
    }
  };

  return {
    can, permissionsLoading, hasAnyAssignmentAccess,
    assignments, total, loading, saving, busyId,
    projectFilterOptions, users,
    search, setSearch, statusFilter, setStatusFilter, projectFilter, setProjectFilter,
    currentPage, setCurrentPage, pageSize, totalPages,
    form, selectedFormRow, showModal,
    handleChange, openAdd, closeModal, handleFormChange, handleSave,
    setStatus,
    usersModalTarget, removeUserTarget, setRemoveUserTarget, removingUser,
    assignedUsers, addUserIds, setAddUserIds, usersLoading,
    openUsersModal, closeUsersModal, addUsersToAssignment, confirmRemoveUserFromAssignment,
    fetchAssignments,

    // Manage Assigned Employees modal's "Add Employees" directory search
    // (backend-searched, never assigner-scoped -- see openUsersModal/
    // searchEligibleUsers above).
    eligibleUsers, eligibleUsersTotal, eligibleUsersLoading, eligibleUsersPage,
    eligibleUsersSearch, searchEligibleUsers, loadMoreEligibleUsers,
    ELIGIBLE_USERS_PAGE_SIZE,

    // Phase 16 (Public Form Links): per-assignee link management + the
    // one-time "here are the links" surface after creating an assignment.
    linkBusyId, generatePublicLink, regeneratePublicLink, revokePublicLink,
    justAssignedLinks, closeJustAssignedLinks, copyToClipboard,
    generatedLinks,

    // Creator-scoped ("Assigned By Me") dataset -- form_assignments.created_by
    // = the authenticated actor, filtered/paginated server-side by the SAME
    // search/statusFilter/projectFilter/currentPage state as `assignments`
    // above. This is what "Assigned By Me" KPI + table must BOTH read from;
    // `assignments`/`total` above stay permission/data-scope visibility and
    // must never be used to answer "did I create this assignment."
    assignedByMeAssignments, assignedByMeTotal, assignedByMeLoading, assignedByMeTotalPages, fetchAssignedByMe,

    // Admin redesign additions (unused by RoleFormAssignmentsPage today,
    // safe/additive either way):
    viewMode, setViewMode, activeTab, setActiveTab,
    visibleAssignments, visibleLoading,
    expandedRowId, expandedDetail, expandedLoading, toggleRowExpand,
    editTarget, editForm, editSaving, openEdit, closeEdit, handleEditFormChange, handleUpdateAssignment,
    dragForms: visibleDragForms, dragFormsLoading, dragFormTypeOptions,
    dragFormSearch, setDragFormSearch,
    dragFormTypeFilter, setDragFormTypeFilter,
    dragProjectFilter, setDragProjectFilter,
    dragModuleFilter, setDragModuleFilter,
    departmentGroups,
    stageAssignmentFromDrop,
  };
}
